import { EMPTY_GUID } from "@utils/helpers";
import { SolutionType } from "@utils/optionsSets/OptionSets";
import styled, { css } from "styled-components";
import { areYearsSimilar, isNullOrEmptyObject } from "utils/helpers";

export const CardContainer = styled.div`
	border-top: 1px solid #c4c4c4;
	:hover {
		cursor: pointer;
	}
	${(props) =>
		props.islast &&
		css`
			border-bottom: 1px solid #c4c4c4;
		`}
`;

export const getPointColors = (type) => {
	switch (type) {
		case SolutionType.QuickWin:
			return { background: "#E7EFFF", border: "#3353E0" };
		case SolutionType.ComplexProject:
			return { background: "#EDEDED", border: "#595959" };
		case SolutionType.NiceToHave:
			return { background: "#FDF7D0", border: "#6F5608" };
		case SolutionType.ToAvoid:
			return { background: "#FDEAE8", border: "#BC2722" };
		default:
			return { background: "#c3c3c3", border: "#000" };
	}
};

export const getMonthNumber = (inputDate) => {
	if (!(inputDate instanceof Date) || isNaN(inputDate)) {
		return "";
	}

	return inputDate.getMonth();
};

export const getAverageScore = ({ data, scoreAttribute }) => {
	if (data.length === 0) return 0;
	let score = 0;
	data.forEach((item) => (score += item[scoreAttribute]));
	return score / data.length;
};

export function sortByName(objects) {
	objects.sort((a, b) => {
		const nameA = a.name?.toLowerCase();
		const nameB = b.name?.toLowerCase();
		if (nameA < nameB) {
			return -1;
		}
		if (nameA > nameB) {
			return 1;
		}
		return 0;
	});

	return objects;
}

export const isBlanksSelected = (data) => data?.find((item) => item?.id === EMPTY_GUID && item?.name === "Blanks");
export const isBlanks = (data) => data?.id === EMPTY_GUID && data?.name === "Blanks";

export const bigNumberFormatter = ({ number, currencySymbol }) => {
	let formatted = "";
	if (number >= 1000000) {
		formatted = `${(number / 1000000).toFixed(1)}M`;
	} else if (number >= 1000) {
		formatted = `${(number / 1000).toFixed(1)}K`;
	}
	if (currencySymbol) formatted += `${currencySymbol} `;
	return formatted;
};

export const getEmotionData = ({ level }) => {
	if (level === 1) return { smiley: "face-frown-open", description: "ASPX_76", backgroundColor: "linear-gradient(2deg, #FFF 6.47%, #FEF2F2 95.32%)", textColor: "#EF4444", borderColor: "#FEE2E2" };
	if (level === 2) return { smiley: "face-frown", description: "ASPX_77", backgroundColor: "linear-gradient(2deg, #FFF 6.47%, #FFF7ED 95.32%)", textColor: "#F97316", borderColor: "#FFEDD5" };
	if (level === 3) return { smiley: "face-meh", description: "ASPX_78", backgroundColor: "linear-gradient(2deg, #FFF 6.47%, #FEFCE8 95.32%);", textColor: "#EAB308", borderColor: "#FEF9C3" };
	if (level === 4) return { smiley: "face-smile", description: "ASPX_79", backgroundColor: "linear-gradient(2deg, #FFF 6.47%, #F0FDF4 95.32%);", textColor: "#22C55E", borderColor: "#BBF7D0" };
	if (level === 5) return { smiley: "face-laugh", description: "ASPX_80", backgroundColor: "linear-gradient(2deg, #FFF 6.47%, #F0FDFA 95.32%);", textColor: "#14B8A6", borderColor: "#CCFBF1" };

	return { smiley: "face-meh-blank", description: "NO_SENTIMENT", backgroundColor: "#fff", textColor: "#BFBFBF", borderColor: "#F0F0F0" };
};

export const isSelectable = (selectedFilters, relatedId) => {
	const isBlank = isBlanksSelected(selectedFilters);
	// If no items selected in the filter
	if (selectedFilters?.length === 0) return false;

	if (isBlank && !relatedId) return true;

	return selectedFilters.map((m) => m.id).includes(relatedId);
};

export const getInteractionsRelatedItems = ({ interactions, allTouchpoints, allMetrics }) => {
	const formatedData = { touchpoints: [{ id: EMPTY_GUID, name: "Blanks" }], metrics: [{ id: EMPTY_GUID, name: "Blanks" }] };
	const touchpointMemory = {};
	const metricMemory = {};

	interactions.map((interaction) => {
		const currentTouchpoint = allTouchpoints?.find((tp) => tp?.id === interaction?.touchpointId);
		const currentMetric = allMetrics?.find((metric) => metric?.id === (interaction?.metricId || interaction?.cxMetricId));

		if (!isNullOrEmptyObject(currentTouchpoint) && !(currentTouchpoint.id in touchpointMemory)) {
			formatedData.touchpoints.push({ id: currentTouchpoint.id, name: currentTouchpoint.name, weight: currentTouchpoint?.weight });
			touchpointMemory[currentTouchpoint.id] = true;
		}
		if (!isNullOrEmptyObject(currentMetric) && !(currentMetric.id in metricMemory)) {
			formatedData.metrics.push({ id: currentMetric.id, name: currentMetric.name });
			metricMemory[currentMetric.id] = true;
		}
	});

	return formatedData;
};

export const getFilteredInteractions = ({ personaInteractions, contactInteractions, cjms, personas, touchpoints = [], metrics = [], date = "" }) => {
	const currentPersonaInteractions = personaInteractions?.filter(
		(interaction) =>
			cjms.map((m) => m.id).includes(interaction?.mapId) &&
			personas.map((persona) => persona.id).includes(interaction?.personaId) &&
			(touchpoints.length === 0 || isSelectable(touchpoints, interaction?.touchpointId))
	);

	const currentContactInteractions = contactInteractions?.filter(
		(interaction) =>
			cjms.map((m) => m.id).includes(interaction?.mapId) &&
			personas.map((persona) => persona.id).includes(interaction?.personaId) &&
			(touchpoints.length === 0 || isSelectable(touchpoints, interaction?.touchpointId)) &&
			(metrics.length === 0 || isSelectable(metrics, interaction?.metricId)) &&
			(date === "" || areYearsSimilar(interaction?.date, date))
	);

	return { currentPersonaInteractions, currentContactInteractions };
};
